import React, { useState, useEffect } from 'react';
import { X, Save } from 'lucide-react';
import { PhoneNumber } from 'types';
import { LANGUAGES } from 'utils/constants';
import { organizationsService, Organization } from 'api/services/organizations.service';
import { useAuth } from 'hooks/useAuth';

interface PhoneModalProps {
  phone: PhoneNumber | null;
  onClose: () => void;
  onSave: (data: Omit<PhoneNumber, 'id'>) => void;
}

const PhoneModal: React.FC<PhoneModalProps> = ({ phone, onClose, onSave }) => {
  const { currentOrganization, user } = useAuth();
  const currentRole = (currentOrganization?.role || user?.role)?.toLowerCase();

  const [formData, setFormData] = useState<Omit<PhoneNumber, 'id'>>({
    dialing_code: '',
    cli_number: '',
    transcribe_name: '',
    operator_id: '',
    service_id: '',
    default_language: 'en',
    routing_type: 'internal',
    sip_trunk_uri: '',
    sip_trunk_username: '',
    sip_trunk_password: '',
    sip_trunk_failover: false,
    trunk_priority: 'trunk_first'
  });

  const [errors, setErrors] = useState<Partial<Record<keyof typeof formData, string>>>({});
  const [businesses, setBusinesses] = useState<Organization[]>([]);
  const [services, setServices] = useState<Organization[]>([]);
  const [loadingOrgs, setLoadingOrgs] = useState(true);

  // Check if user is business admin (operator_admin) - they can only edit display name
  const isBusinessAdmin = currentRole === 'operator_admin';
  const isEditingAsBusinessAdmin = isBusinessAdmin && phone !== null;

  // Prefill on edit. The stored operator_id/service_id may be a legacy NAME code
  // (e.g. "TEEOFF"/"catchMyCall" from a direct DB import) instead of an org UUID, in
  // which case the controlled <select> can't match a UUID-keyed <option> and falls
  // back to the placeholder. Resolve each to the matching option id: exact id →
  // match by businessId/name → fall back to the owning org UUID (business/operator)
  // or that business's parent service UUID (service). Re-runs when the option lists
  // finish loading so resolution uses populated businesses/services.
  useEffect(() => {
    if (!phone) return;
    const { id, ...phoneData } = phone;
    const orgId = phone.organization_id;

    const resolveBusiness = (val?: string): string => {
      if (val && businesses.some((b) => b.id === val)) return val;
      const lc = (val || '').toLowerCase();
      const byName = businesses.find((b) => b.businessId === val || b.name?.toLowerCase() === lc);
      if (byName) return byName.id;
      if (orgId && businesses.some((b) => b.id === orgId)) return orgId; // owning business
      return val || '';
    };
    const resolveService = (val?: string): string => {
      if (val && services.some((s) => s.id === val)) return val;
      const lc = (val || '').toLowerCase();
      const byName = services.find((s) => s.businessId === val || s.name?.toLowerCase() === lc);
      if (byName) return byName.id;
      const owningBiz = businesses.find((b) => b.id === orgId); // derive via org hierarchy
      const parent = owningBiz?.parentOrganizationId;
      if (parent && services.some((s) => s.id === parent)) return parent;
      return val || '';
    };

    setFormData({
      ...phoneData,
      operator_id: resolveBusiness(phoneData.operator_id),
      service_id: resolveService(phoneData.service_id),
    });
  }, [phone, businesses, services]);

  // Load organizations based on user role
  useEffect(() => {
    const loadOrganizations = async () => {
      try {
        setLoadingOrgs(true);

        // Load services (always visible to all admins)
        const servicesResponse = await organizationsService.getServiceProviders();
        if (servicesResponse.success && servicesResponse.data) {
          setServices(servicesResponse.data);
        }

        // Load businesses based on user role
        if (currentRole === 'master_admin' || currentRole === 'super_admin') {
          // Master admin and Super admin see all businesses
          const businessesResponse = await organizationsService.getAllBusinesses();
          if (businessesResponse.success && businessesResponse.data) {
            setBusinesses(businessesResponse.data);
          }
        } else if (currentRole === 'service_admin' && currentOrganization?.id) {
          // Service admin sees businesses under their service
          const businessesResponse = await organizationsService.getBusinessesByServiceProvider(currentOrganization.id);
          if (businessesResponse.success && businessesResponse.data) {
            setBusinesses(businessesResponse.data);
          }
        } else if (currentRole === 'operator_admin' && currentOrganization?.id) {
          // Operator admin sees only their own business
          setBusinesses([{
            id: currentOrganization.id,
            name: currentOrganization.name,
            type: currentOrganization.type as 'BUSINESS',
            businessId: currentOrganization.business_id || '',
            active: true,
            createdAt: '',
            updatedAt: ''
          }]);
        }
      } catch (error) {
        console.error('Error loading organizations:', error);
      } finally {
        setLoadingOrgs(false);
      }
    };

    loadOrganizations();
  }, [currentRole, currentOrganization]);

  const validate = () => {
    const newErrors: Partial<Record<keyof typeof formData, string>> = {};

    // If business admin is editing, only validate display name
    if (isEditingAsBusinessAdmin) {
      if (!formData.transcribe_name) {
        newErrors.transcribe_name = 'Display name is required';
      }
    } else {
      // Full validation for super_admin and service_admin
      if (!formData.dialing_code) {
        newErrors.dialing_code = 'Dialing code is required';
      }
      if (!formData.cli_number) {
        newErrors.cli_number = 'CLI number is required';
      }
      if (!formData.transcribe_name) {
        newErrors.transcribe_name = 'Display name is required';
      }
      if (!formData.operator_id) {
        newErrors.operator_id = 'Operator ID is required';
      }
      if (!formData.service_id) {
        newErrors.service_id = 'Service ID is required';
      }

      // Validate SIP trunk configuration when sip_trunk or hybrid routing is selected
      if (formData.routing_type === 'sip_trunk' || formData.routing_type === 'hybrid') {
        if (!formData.sip_trunk_uri || !formData.sip_trunk_uri.trim()) {
          newErrors.sip_trunk_uri = 'SIP Trunk URI is required for SIP trunk routing';
        } else if (!formData.sip_trunk_uri.startsWith('sip:')) {
          newErrors.sip_trunk_uri = 'SIP URI must start with "sip:"';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      // Strip server-managed / read-only fields before saving. created_at & updated_at
      // are serialized OUT as epoch millis but the API can't parse them back into
      // LocalDateTime (it 400s: "raw timestamp not allowed for java.time.LocalDateTime").
      // organization_id/organization_name are derived server-side from operator_id.
      const { created_at, updated_at, organization_id, organization_name, active,
        sip_trunk_password_set, ...payload } = formData as Record<string, unknown>;

      // A BLANK password means "leave the stored one alone", never "erase it".
      //
      // The API returns sip_trunk_password write-only, so editing an existing number
      // loads this field empty. Without this, saving any unrelated change — a label —
      // would PUT an empty password over a working one and break that trunk's SIP
      // authentication. sip_trunk_password_set is read-only and must not be sent back.
      const pwd = payload.sip_trunk_password;
      if (typeof pwd !== 'string' || pwd.trim() === '') delete payload.sip_trunk_password;

      onSave(payload as Omit<PhoneNumber, 'id'>);
    }
  };

  const handleChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        <div className="p-6 border-b flex items-center justify-between flex-shrink-0">
          <h3 className="text-xl font-bold">
            {phone ? 'Edit' : 'Add'} Phone Number
          </h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          {isEditingAsBusinessAdmin && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-blue-800">
                As a Business Admin, you can only edit the Display Name of phone numbers.
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Dialing Code *
              </label>
              <input
                type="text"
                value={formData.dialing_code}
                onChange={(e) => handleChange('dialing_code', e.target.value)}
                disabled={isEditingAsBusinessAdmin}
                className={`w-full px-3 py-2 border rounded-lg ${
                  errors.dialing_code ? 'border-red-500' : 'border-gray-300'
                } ${isEditingAsBusinessAdmin ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                placeholder="1"
              />
              {errors.dialing_code && (
                <p className="text-xs text-red-500 mt-1">{errors.dialing_code}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                CLI Number *
              </label>
              <input
                type="text"
                value={formData.cli_number}
                onChange={(e) => handleChange('cli_number', e.target.value)}
                disabled={isEditingAsBusinessAdmin}
                className={`w-full px-3 py-2 border rounded-lg ${
                  errors.cli_number ? 'border-red-500' : 'border-gray-300'
                } ${isEditingAsBusinessAdmin ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                placeholder="+911234567890"
              />
              {errors.cli_number && (
                <p className="text-xs text-red-500 mt-1">{errors.cli_number}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Display Name *
            </label>
            <input
              type="text"
              value={formData.transcribe_name}
              onChange={(e) => handleChange('transcribe_name', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg ${
                errors.transcribe_name ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Customer Support Line"
            />
            {errors.transcribe_name && (
              <p className="text-xs text-red-500 mt-1">{errors.transcribe_name}</p>
            )}
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Business / Operator *
              </label>
              <select
                value={formData.operator_id}
                onChange={(e) => handleChange('operator_id', e.target.value)}
                disabled={loadingOrgs || isEditingAsBusinessAdmin}
                className={`w-full px-3 py-2 border rounded-lg ${
                  errors.operator_id ? 'border-red-500' : 'border-gray-300'
                } disabled:bg-gray-100 disabled:cursor-not-allowed`}
              >
                <option value="">Select Business</option>
                {businesses.map((business) => (
                  <option key={business.id} value={business.id}>
                    {business.name} ({business.businessId})
                  </option>
                ))}
              </select>
              {errors.operator_id && (
                <p className="text-xs text-red-500 mt-1">{errors.operator_id}</p>
              )}
              {loadingOrgs && (
                <p className="text-xs text-gray-500 mt-1">Loading businesses...</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Service Provider *
              </label>
              <select
                value={formData.service_id}
                onChange={(e) => handleChange('service_id', e.target.value)}
                disabled={loadingOrgs || isEditingAsBusinessAdmin}
                className={`w-full px-3 py-2 border rounded-lg ${
                  errors.service_id ? 'border-red-500' : 'border-gray-300'
                } disabled:bg-gray-100 disabled:cursor-not-allowed`}
              >
                <option value="">Select Service</option>
                {services.map((service) => (
                  <option key={service.id} value={service.id}>
                    {service.name}
                  </option>
                ))}
              </select>
              {errors.service_id && (
                <p className="text-xs text-red-500 mt-1">{errors.service_id}</p>
              )}
              {loadingOrgs && (
                <p className="text-xs text-gray-500 mt-1">Loading services...</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Default Language
            </label>
            <select
              value={formData.default_language}
              onChange={(e) => handleChange('default_language', e.target.value)}
              disabled={isEditingAsBusinessAdmin}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg ${
                isEditingAsBusinessAdmin ? 'bg-gray-100 cursor-not-allowed' : ''
              }`}
            >
              {LANGUAGES.map(lang => (
                <option key={lang.value} value={lang.value}>
                  {lang.label}
                </option>
              ))}
            </select>
          </div>

          {/* SIP Trunking Configuration */}
          <div className="border-t pt-4 space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16l2.879-2.879m0 0a3 3 0 104.243-4.242 3 3 0 00-4.243 4.242zM21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h4 className="text-lg font-semibold text-gray-900">Call Routing Configuration</h4>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Routing Type
              </label>
              <select
                value={formData.routing_type || 'internal'}
                onChange={(e) => handleChange('routing_type', e.target.value as any)}
                disabled={isEditingAsBusinessAdmin}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg ${
                  isEditingAsBusinessAdmin ? 'bg-gray-100 cursor-not-allowed' : ''
                }`}
              >
                <option value="internal">Internal (IVR / Voice Agent)</option>
                <option value="sip_trunk">SIP Trunk (Forward to External SIP)</option>
                <option value="hybrid">Hybrid (Both IVR and SIP Trunk)</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {formData.routing_type === 'internal' && 'Calls handled by your IVR Application or AI Voice Agent'}
                {formData.routing_type === 'sip_trunk' && 'Calls forwarded directly to external SIP trunk'}
                {formData.routing_type === 'hybrid' && 'Calls can be routed to both IVR and SIP trunk based on priority'}
              </p>
            </div>

            {/* SIP Trunk Configuration - Show when sip_trunk or hybrid is selected */}
            {(formData.routing_type === 'sip_trunk' || formData.routing_type === 'hybrid') && (
              <>
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      SIP Trunk URI *
                    </label>
                    <input
                      type="text"
                      value={formData.sip_trunk_uri || ''}
                      onChange={(e) => handleChange('sip_trunk_uri', e.target.value)}
                      disabled={isEditingAsBusinessAdmin}
                      className={`w-full px-3 py-2 border rounded-lg ${
                        errors.sip_trunk_uri ? 'border-red-500' : 'border-gray-300'
                      } ${isEditingAsBusinessAdmin ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      placeholder="sip:+1234567890@trunk.yourprovider.com"
                    />
                    {errors.sip_trunk_uri && (
                      <p className="text-xs text-red-500 mt-1">{errors.sip_trunk_uri}</p>
                    )}
                    <p className="text-xs text-gray-600 mt-1">
                      Enter the SIP URI for your external trunk provider
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        SIP Username
                      </label>
                      <input
                        type="text"
                        value={formData.sip_trunk_username || ''}
                        onChange={(e) => handleChange('sip_trunk_username', e.target.value)}
                        disabled={isEditingAsBusinessAdmin}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-lg ${
                          isEditingAsBusinessAdmin ? 'bg-gray-100 cursor-not-allowed' : ''
                        }`}
                        placeholder="trunk_user"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        SIP Password
                      </label>
                      <input
                        type="password"
                        value={formData.sip_trunk_password || ''}
                        onChange={(e) => handleChange('sip_trunk_password', e.target.value)}
                        disabled={isEditingAsBusinessAdmin}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-lg ${
                          isEditingAsBusinessAdmin ? 'bg-gray-100 cursor-not-allowed' : ''
                        }`}
                        placeholder={formData.sip_trunk_password_set
                          ? 'Stored — leave blank to keep it'
                          : 'Not set'}
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        {formData.sip_trunk_password_set
                          ? 'A password is stored. It is never shown; leave this blank to keep it, or type a new one to replace it.'
                          : 'No password stored for this trunk.'}
                      </p>
                    </div>
                  </div>

                  {formData.routing_type === 'hybrid' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Priority
                      </label>
                      <select
                        value={formData.trunk_priority || 'trunk_first'}
                        onChange={(e) => handleChange('trunk_priority', e.target.value as any)}
                        disabled={isEditingAsBusinessAdmin}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-lg ${
                          isEditingAsBusinessAdmin ? 'bg-gray-100 cursor-not-allowed' : ''
                        }`}
                      >
                        <option value="trunk_first">Try SIP Trunk First, then IVR</option>
                        <option value="internal_first">Try IVR First, then SIP Trunk</option>
                      </select>
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="sip_trunk_failover"
                      checked={formData.sip_trunk_failover || false}
                      onChange={(e) => handleChange('sip_trunk_failover', e.target.checked as any)}
                      disabled={isEditingAsBusinessAdmin}
                      className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                    />
                    <label htmlFor="sip_trunk_failover" className="text-sm text-gray-700">
                      Enable automatic failover to {formData.routing_type === 'hybrid' ? 'alternate routing' : 'internal IVR'} if SIP trunk fails
                    </label>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="p-6 border-t flex justify-end gap-3 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="tl-btn tl-btn-primary"
          >
            <Save className="w-4 h-4" />
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default PhoneModal;